import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, CheckCircle2, ArrowRight, BadgeDollarSign, ShieldCheck, Clock } from "lucide-react";
import { PHONE, PHONE_HREF } from "@/data/site";
import { ALL_SERVICES, WH_SERVICES } from "@/data/services";
import { QuoteForm } from "@/components/QuoteForm";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { BrandsMarquee } from "@/components/BrandsMarquee";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function ServicePage() {
  const { slug } = useParams();
  const service = ALL_SERVICES.find((s) => s.slug === slug);

  useEffect(() => {
    if (service) document.title = `${service.h1} | Irvine Water Heater Man`;
  }, [service]);

  if (!service) return <Navigate to="/" replace />;
  const isWH = WH_SERVICES.some((s) => s.slug === slug);
  const related = WH_SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div>
      <section className="navy-grain text-white px-4 sm:px-6 pt-14 sm:pt-20 pb-16" data-testid="service-hero">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-start">
          <div>
            <MaskedLine>
              <span className="inline-flex items-center gap-2 bg-[#FF6500]/15 border border-[#FF6500]/30 text-[#FF9A5C] font-heading font-bold text-xs uppercase tracking-widest rounded-full px-4 py-1.5">
                <Clock className="w-3.5 h-3.5" /> Same-Day Service · 24/7 Emergency
              </span>
            </MaskedLine>
            <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 mb-6">
              <MaskedLine delay={0.12}>{service.h1}</MaskedLine>
            </h1>
            <MaskedLine delay={0.25}>
              <p className="text-white/70 text-lg leading-relaxed max-w-xl">{service.short}</p>
            </MaskedLine>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <a href={PHONE_HREF} data-testid="service-call-btn" className="inline-flex items-center gap-3 bg-[#FF6500] text-white font-heading font-bold text-lg px-7 py-4 rounded-full hover:bg-[#E85C0D] hover:scale-105 transition-[background-color,transform]">
                <Phone className="w-5 h-5" /> Call {PHONE}
              </a>
              <div className="flex items-center gap-2 text-white/70">
                <BadgeDollarSign className="w-5 h-5 text-[#FF6500]" />
                <span className="font-heading font-bold">From {service.priceFrom}</span>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl" data-testid="service-quote-card">
            <h2 className="font-heading font-extrabold text-2xl text-gray-900 mb-1">Get a Free Estimate</h2>
            <p className="text-gray-500 text-sm mb-5">20 seconds. A specialist calls you right back.</p>
            <QuoteForm idPrefix="service" />
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white" data-testid="service-content">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.5fr_1fr] gap-14">
          <div>
            <Reveal>
              <p className="text-gray-700 text-lg leading-relaxed mb-10">{service.intro}</p>
            </Reveal>
            {service.body.map((b, i) => (
              <Reveal key={b.heading} delay={i * 0.05}>
                <div className="mb-10">
                  <div className="flex items-baseline gap-4 mb-3">
                    <span className="font-heading font-black text-3xl text-gray-200 select-none">{String(i + 1).padStart(2, "0")}</span>
                    <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight text-gray-900">{b.heading}</h2>
                  </div>
                  <p className="text-gray-600 leading-relaxed">{b.text}</p>
                </div>
              </Reveal>
            ))}
            <Reveal>
              <div className="bg-[#FAFAFA] border border-gray-200 rounded-2xl p-7 mb-12">
                <h3 className="font-heading font-bold text-xl text-gray-900 mb-4">Signs You Need This Service</h3>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {service.signs.map((s) => (
                    <li key={s} className="flex items-start gap-2.5 text-gray-700">
                      <CheckCircle2 className="w-5 h-5 text-[#FF6500] mt-0.5 shrink-0" /> {s}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal>
              <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight text-gray-900 mb-6">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="mb-4" data-testid="service-faq">
                {service.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`}>
                    <AccordionTrigger className="font-heading font-bold text-left text-gray-900 hover:text-[#FF6500]">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-gray-600 leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
          <aside className="space-y-6 lg:sticky lg:top-28 self-start">
            <Reveal>
              <div className="rounded-2xl overflow-hidden">
                <img src={service.image} alt={service.name} className="w-full h-56 object-cover" />
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <div className="bg-[#0B192C] text-white rounded-2xl p-7">
                <p className="font-heading font-bold text-lg mb-1">{service.name}</p>
                <p className="font-heading font-black text-4xl text-[#FF6500] mb-1">From {service.priceFrom}</p>
                <p className="text-white/60 text-sm mb-5">{service.priceNote}</p>
                <div className="flex items-center gap-2 text-sm text-white/70 mb-2">
                  <ShieldCheck className="w-4 h-4 text-[#FF6500]" /> Licensed &amp; insured · flat-rate quote in writing
                </div>
                <a href={PHONE_HREF} data-testid="sidebar-call-btn" className="mt-4 flex items-center justify-center gap-2 bg-[#FF6500] text-white font-heading font-bold py-3.5 rounded-full hover:bg-[#E85C0D] transition-colors">
                  <Phone className="w-4 h-4" /> {PHONE}
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="border border-gray-200 rounded-2xl p-7">
                <p className="font-heading font-bold text-gray-900 mb-4">Related Services</p>
                <ul className="space-y-3">
                  {related.map((r) => (
                    <li key={r.slug}>
                      <Link to={`/services/${r.slug}`} className="group flex items-center justify-between text-gray-700 hover:text-[#FF6500] font-semibold text-sm transition-colors">
                        {r.name} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>

      {isWH && <BrandsMarquee />}
      <CTABand idPrefix="service-cta" />
    </div>
  );
}
